import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import styles from './InstitutionSubpages.module.scss';
import { updateClaims } from '../../../utils/authStore';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { graphql } from 'client/gql';
import { InstitutionCoursesTable } from 'components/Courses/tables/InstitutionCoursesTable';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { CourseModal } from 'components/Courses/modals/CourseModal';
import { CourseSettingsModal } from 'components/Courses/modals/CourseSettingsModal';
import { CourseProfileModal } from 'components/Courses/modals/CourseProfileModal';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useCourses } from 'components/Courses/hooks/useCourses';
import { GroupType } from '@bp/bp-graphql-types';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromInstitutionCourses($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const InstitutionCoursesSubpageQueryDocument = graphql(`
  query InstitutionCoursesSubpageQueryData($courseWhere: GroupWhere!, $profile: ID!) {
    ...PermissionCheckerFields
    ...UseCoursesFields
  }
`);

export const InstitutionCoursesSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const navigate = useNavigate();

  const { ConfirmationDialog, confirmDelete } = useCourses();

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);

  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [courseModalOpen, setCourseModalOpen] = useState<boolean>(false);
  const [profilesModalOpen, setProfilesModalOpen] = useState<boolean>(false);
  const [courseUuid, setCourseUuid] = useState<string | null>(null);
  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);

  const [, deleteCourse] = useMutation(DeleteGroupMutationDocument);

  const [{ data, error }] = useQuery({
    query: InstitutionCoursesSubpageQueryDocument,
    variables: {
      profile: profileUuid,
      courseWhere: {
        type: GroupType.Course,
        organization: { uuid: organizationUuid },
        schoolYear: { uuid: selectedYearUuid },
      },
    },
  });

  if (error) showUserDbErrorToast();

  const permissions = usePermissionChecker(data);

  function handleSettings(courseUuid: string) {
    setSettingsModalOpen(true);
    setCourseUuid(courseUuid);
  }

  function handleEdit(uuid: string) {
    setCourseModalOpen(true);
    setCourseUuid(uuid);
  }

  function handleProfilesEdit(uuid: string) {
    setProfilesModalOpen(true);
    setCourseUuid(uuid);
  }

  function handleYearSelect(uuid: string) {
    setSelectedYearUuid(uuid);
  }

  function handleAdd() {
    setCourseUuid(null);
    setCourseModalOpen(true);
  }

  async function handleDelete(uuid: string) {
    const result = await confirmDelete();
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteCourse({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  function handleNavigate(uuid: string) {
    navigate('/courses/' + uuid);
  }

  function handleModalClose() {
    setCourseModalOpen(false);
    setSettingsModalOpen(false);
    setProfilesModalOpen(false);
    setCourseUuid(null);
  }

  return (
    <BpSubpage
      className={styles.courses}
      isForbidden={!permissions?.canListInstitutionCourses({ uuid: organizationUuid })}
    >
      <InstitutionCoursesTable
        loading={tableLoading}
        query={data}
        yearUuid={selectedYearUuid}
        onYearSelect={handleYearSelect}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onProfilesEdit={handleProfilesEdit}
        onSettings={handleSettings}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        permissions={permissions}
      />

      <CourseModal
        isOpen={courseModalOpen}
        courseUuid={courseUuid}
        schoolYearUuid={selectedYearUuid}
        onClose={handleModalClose}
        canCollaborate
        forceUserAsEditor={false}
      />

      {courseUuid && (
        <CourseProfileModal
          isOpen={profilesModalOpen}
          onClose={async () => {
            await updateClaims(() => setClaimsTrigger(!claimsTrigger));
            handleModalClose();
          }}
          courseUuid={courseUuid}
        />
      )}

      {courseUuid && (
        <CourseSettingsModal isOpen={settingsModalOpen} courseUuid={courseUuid} onClose={handleModalClose} />
      )}

      <ConfirmationDialog />
    </BpSubpage>
  );
};
